import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { Wordmark } from "@/components/brand/Logo";
import { useSessionStore } from "@/stores/sessionStore";

const NAV = [
  { to: "/how-it-works", label: "How it works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
];

interface HeaderProps {
  thin?: boolean;
  connected?: boolean;
  // Landing opts in to the translucent, blurred nav that stays pinned
  // as the page scrolls. Other pages keep the static parchment bar.
  sticky?: boolean;
}

export function Header({ thin = false, connected, sticky = false }: HeaderProps) {
  const navigate = useNavigate();
  const resetSession = useSessionStore((s) => s.resetSession);
  const [menuOpen, setMenuOpen] = useState(false);

  function handleStartFresh() {
    setMenuOpen(false);
    resetSession();
    navigate("/session");
  }

  const headerClass = sticky
    ? "sticky top-0 z-30 border-b-[0.5px] border-line bg-parchment/85 backdrop-blur-[12px]"
    : `bg-parchment ${thin ? "border-b border-line" : ""}`;

  const navPadding = sticky ? "py-4" : thin ? "py-4" : "py-6";
  const navBorder = !sticky && !thin ? "border-b border-line" : "";

  return (
    <header className={headerClass}>
      <nav
        className={`mx-auto flex max-w-[1180px] items-center justify-between gap-4 px-6 sm:px-8 ${navPadding} ${navBorder}`}
      >
        <Link to="/" aria-label="Revetra home">
          <Wordmark size={30} textClassName="text-[22px]" />
        </Link>

        <div className="hidden items-center gap-7 text-[14px] font-medium text-ink-muted min-[981px]:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `transition-colors hover:text-ink ${isActive ? "text-ink" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
          {connected !== undefined && (
            <span
              className="session-status"
              aria-label={connected ? "connected" : "disconnected"}
              style={connected ? undefined : { color: "rgb(var(--color-warning))" }}
            >
              {connected ? "Connected" : "Reconnecting…"}
            </span>
          )}
          <button
            type="button"
            onClick={handleStartFresh}
            className="rounded-full bg-ink px-[18px] py-2 text-[13px] font-medium text-parchment transition hover:bg-black"
          >
            Begin
          </button>
        </div>

        {/* Mobile: Begin button + disclosure menu for the nav links */}
        <div className="flex items-center gap-2 min-[981px]:hidden">
          <button
            type="button"
            onClick={handleStartFresh}
            className="rounded-full bg-ink px-[18px] py-2 text-[13px] font-medium text-parchment"
          >
            Begin
          </button>
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink transition hover:bg-parchment-soft"
          >
            {menuOpen ? (
              <X size={20} strokeWidth={1.75} aria-hidden />
            ) : (
              <Menu size={20} strokeWidth={1.75} aria-hidden />
            )}
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div
          id="mobile-nav"
          className="border-t-[0.5px] border-line bg-parchment px-6 py-4 sm:px-8 min-[981px]:hidden"
        >
          <ul className="m-0 list-none space-y-1 p-0">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-2.5 text-[15px] font-medium transition-colors hover:bg-parchment-soft hover:text-ink ${
                      isActive ? "text-ink" : "text-ink-muted"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
          {connected !== undefined && (
            <p
              className="session-status mt-3 px-3"
              aria-label={connected ? "connected" : "disconnected"}
              style={connected ? undefined : { color: "rgb(var(--color-warning))" }}
            >
              {connected ? "Connected" : "Reconnecting…"}
            </p>
          )}
        </div>
      )}
    </header>
  );
}
